import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  forwardRef,
  ViewChild
} from '@angular/core';
import { DropdownService } from './dropdown.service';
import { Subscription } from 'rxjs';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
@Component({
    selector: 'app-dropdown',
    templateUrl: './dropdown.component.html',
    styleUrls: ['./dropdown.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => DropdownComponent)
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: DropdownComponent
        }
    ],
    standalone: false
})
export class DropdownComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  @ViewChild('input') inputElement: ElementRef;
  showingMenu = false;
  hasFocus = false;
  typeAhead = '';
  typeAheadIndex = null;
  timer: any;
  private dropdownSubscription: Subscription;
  @Input() disabled: boolean;
  @Input() expandDirection: any;
  @Input() required: any;
  @Input() choice = {};
  @Input() key: any;
  @Input() options: any;
  @Input() requiredError: boolean = false;
  @Output() selectedDropdownItem = new EventEmitter<Object>();
  @Input() label?: string = ''

  touched = false;
  onTouched = () => {};
  onChange = (choice) => {};

  constructor(
    private readonly elementRef: ElementRef,
    private readonly dropdownService: DropdownService
  ) {}

  ngOnInit(): void {
    this.dropdownSubscription = this.dropdownService.documentClicked.subscribe(
      (target) => this.handleClick(target)
    );
  }

  ngOnDestroy(): void {
    this.dropdownSubscription?.unsubscribe();
  }

  handleClick = (target) => {
    if (!this.elementRef.nativeElement.contains(target)) {
      this.showingMenu = false;
    }
  };

  menuOptionBlur = (e) => {
    if (!((e.relatedTarget?.className?.includes('menu-option')) || (e.relatedTarget?.className?.includes('dropdown-input')))) {
      this.showingMenu = false;
    }
  }

  onBlur = (e) => {
    this.markAsTouched();
    this.hasFocus = false;
    if (!e.relatedTarget?.className?.includes('menu-option')) {
      this.showingMenu = false;
    }
  };

  downArrow = (e) => {
    e.preventDefault();
    if (e.target.nextElementSibling) {
      e.target.nextElementSibling.focus()
    }
  }

  upArrow = (e) => {
    e.preventDefault();
    if (e.target.previousElementSibling) {
      e.target.previousElementSibling.focus()
    }
  }

  onMenuOptionKeypress = (option, e) => {
    e.preventDefault();
    if (e.code === 'Enter' || e.code === 'NumpadEnter') {
      this.select(option);
    } else if (e.code !== 'Tab' && e.code !== 'ArrowUp' && e.code != 'ArrowDown' && ((e.keyCode >= 48 && e.keyCode <= 57) || (e.keyCode >= 65 && e.keyCode <= 90))) {
      this.typeAhead += e.key.toLowerCase();
      clearTimeout(this.timer);
      if (this.typeAhead.length > 0) {
        this.timer = setTimeout(() => {
          this.typeAhead = '';
        }, 1000)
      }
      const findIndex = this.options.findIndex((option) => {
        if (this.key) {
          return option[this.key].toLowerCase().startsWith(this.typeAhead)
        } else if (typeof option === 'string') {
          return option.toLowerCase().startsWith(this.typeAhead.toLowerCase())
        } else if (typeof option === 'number') {
          return option.toString().startsWith(this.typeAhead);
        }
      });
      this.typeAheadIndex = findIndex;
      if (findIndex >= 0) {
        let elements = this.elementRef.nativeElement.children[0].children;
        let menu = elements[1];
        menu.scrollTop = findIndex * menu.children[0].offsetHeight;
        menu.children[findIndex].focus();
      }
    } else if (e.code === 'Escape') {
      this.showingMenu = false;
    }
  };

  inputKeyDown(e: KeyboardEvent): void {
    // want to prevent space from scrolling page, but still allow tab
    if (e.code === 'Space') {
      e.preventDefault();
    }
  }

  onFocus = (e) => {
    if (e.code === 'Space' || e.code === 'Enter') {
      if (!this.showingMenu) {
        e.stopPropagation();
        this.showMenu();
      }
      e.preventDefault();
    }
  }

  showMenu = () => {
    this.showingMenu = true;

    // make sure dropdown fits vertically within visible viewport
    let elements = this.elementRef.nativeElement.children[0].children;
    let menu = elements[1];

    if (menu === undefined) {
      setTimeout(() => {
        this.showMenu();
      }, 10);
    } else {
      menu?.children[0]?.focus();
      let rect = menu.getBoundingClientRect();
      if (rect.y + rect.height > window.innerHeight) {
        let bottom = rect.y + rect.height;
        menu.style.top = window.innerHeight - bottom + 'px';
      }
    }
  };

  select = (option) => {
    this.choice = option;
    this.showingMenu = false;
    this.selectedDropdownItem.emit(option);
    this.onChange(this.key ? this.choice[this.key] : this.choice);
    this.inputElement.nativeElement.focus();
  };

  // start control value accessor interface
  writeValue(obj: any): void {
    this.choice = obj;
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // start validator interface
  validate(control: AbstractControl<any, any>): ValidationErrors | null {
    return null;
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.onChange = fn;
  }
}
